import { Modal, ModalProps } from '@copyrightagent/basic-components'
import { getCaseActions } from 'hooks/useCase'
import React, {
	Dispatch,
	ReactElement,
	SetStateAction,
	useEffect,
	useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { submitCase, getImagesBySearch } from 'routes/Private/Cases/api'
import { toast } from 'store/utils'
import { getSelectedClient } from 'store/utils'
import SearchImagesStep from './createCaseSteps/SearchImagesStep'

interface CreateCaseModalProps {
	opened: boolean
	setDialogOpened: Dispatch<SetStateAction<boolean>>
}

const CreateCaseModal = ({
	opened,
	setDialogOpened,
}: CreateCaseModalProps): ReactElement => {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const [isPending, setIsPending] = useState<boolean>(false)
	const { fetchSubmittedCases } = getCaseActions()

	const handleClose = () => {
		setIsPending(false)
		setOpen(false)
		setDialogOpened(false)
	}

	const handleSearchImages = async (searchTerm: string) => {
		const client = getSelectedClient()
		if (!client?.client_id) {
			toast(t('createCase/noClient') || 'No client selected', 'error')
			return []
		}

		try {
			const response = await getImagesBySearch({
				search: searchTerm,
			})

			if (response.isSuccessful && response.data) {
				return response.data
			}
			return []
		} catch (error) {
			console.error('Failed to search images:', error)
			toast(t('createCase/searchFailed') || 'Failed to search images', 'error')
			return []
		}
	}

	const handleSubmit = async (caseUrl: string, imageId: string) => {
		setIsPending(true)
		try {
			const result = await submitCase(caseUrl, imageId)
			if (result.isSuccessful) {
				toast(t('createCase/success') || 'Case submitted successfully', 'info')
				fetchSubmittedCases(0, 40)
				handleClose()
			} else {
				toast(t('createCase/fail') || 'Failed to submit case', 'error')
			}
		} catch (error) {
			console.error('Failed to submit case:', error)
			toast(t('createCase/fail') || 'Failed to submit case', 'error')
		} finally {
			setIsPending(false)
		}
	}

	useEffect(() => {
		setOpen(opened)
	}, [opened])

	const defaultModalData: Pick<ModalProps, 'onClose' | 'open' | 'size'> = {
		open,
		onClose: handleClose,
		size: 'large',
	}

	const [modalData, _setModalData] = useState<ModalProps>({
		...defaultModalData,
		children: <></>,
		title: '',
		titleText: '',
	})

	const setModalData = (
		data: Pick<ModalProps, 'title' | 'titleText' | 'children'>,
	): void =>
		_setModalData({
			...defaultModalData,
			...data,
		})

	useEffect(() => {
		setModalData({
			title: t('createCase/submit/title') || 'Submit a Case',
			titleText:
				t('createCase/submit/description') ||
				'Enter the URL and select the image for this case',
			children: (
				<SearchImagesStep
					handleClose={handleClose}
					handleSubmit={handleSubmit}
					onSearch={handleSearchImages}
					isPending={isPending}
				/>
			),
		})
	}, [open, isPending])

	return <Modal {...modalData} />
}
export default CreateCaseModal
